import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cross-sell strip for the cart drawer.
 *
 * A custom element rather than an inline script because the drawer markup only
 * exists while the cart has items. Going from empty to non-empty re-renders the
 * cart section and morphs it in, and script tags inside morphed HTML never
 * execute — so an inline script runs exactly once, on a page that loaded with a
 * full cart, and never again. Registering the class here means connectedCallback
 * fires every time the element is morphed into the document.
 *
 * Cart state is read from /cart.js rather than data attributes, so a refresh
 * never depends on whether the morph has landed yet.
 */
class CartCrossSell extends HTMLElement {
  #abortController = null;
  #listening = false;

  connectedCallback() {
    this.list = this.querySelector('[data-cart-cross-sell-list]');
    if (!this.list) return;

    if (!this.#listening) {
      this.addEventListener('click', this.#handleClick);
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
      this.#listening = true;
    }

    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#abortController?.abort();
    this.#listening = false;
  }

  get cartAddUrl() {
    return this.dataset.cartAddUrl || '/cart/add.js';
  }

  get cartUrl() {
    return this.dataset.cartUrl || '/cart.js';
  }

  get currency() {
    return this.dataset.currency || 'USD';
  }

  get limit() {
    return parseInt(this.dataset.limit, 10) || 6;
  }

  get show() {
    return parseInt(this.dataset.show, 10) || 4;
  }

  /**
   * The event fires as soon as an add starts, so the work waits on its promise —
   * otherwise the refresh would read the cart as it was before the line landed.
   */
  #handleCartUpdate = (event) => {
    const done = event.promise ?? Promise.resolve();
    done.then(() => this.refresh()).catch(() => {});
  };

  #handleClick = (event) => {
    const button = event.target.closest('.cart-cross-sell__add');
    if (button) this.#addToCart(button);
  };

  #formatMoney(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency }).format(cents / 100);
  }

  #escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (character) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  #requestRecommendations(seedId, intent, signal) {
    return fetch(`/recommendations/products.json?product_id=${seedId}&limit=${this.limit}&intent=${intent}`, {
      signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => (data && data.products ? data.products : []))
      .catch(() => []);
  }

  /** Buyable variants only, and nothing already sitting in the cart. */
  #usable(products, cartProductIds) {
    return products
      .filter((product) => product.available && !cartProductIds.includes(String(product.id)))
      .map((product) => {
        const variant = (product.variants || []).find((candidate) => candidate.available);
        return variant ? { product, variant } : null;
      })
      .filter(Boolean)
      .slice(0, this.show);
  }

  #render(entries) {
    this.list.innerHTML = entries
      .map(({ product, variant }) => {
        const image = product.featured_image
          ? product.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_200x$1$2')
          : '';
        const title = this.#escapeHtml(product.title);

        return `
          <li class="cart-cross-sell__item">
            <a class="cart-cross-sell__link" href="${product.url}">
              <span class="cart-cross-sell__media">
                ${image ? `<img src="${image}" alt="" loading="lazy" width="56" height="56">` : ''}
              </span>
              <span class="cart-cross-sell__info">
                <span class="cart-cross-sell__title">${title}</span>
                <span class="cart-cross-sell__price">${this.#formatMoney(product.price)}</span>
              </span>
            </a>
            <button
              type="button"
              class="cart-cross-sell__add"
              data-variant-id="${variant.id}"
              data-product-id="${product.id}"
              aria-label="Add ${title} to cart"
            >+</button>
          </li>
        `;
      })
      .join('');

    this.hidden = false;
  }

  async refresh() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      const cart = await fetch(this.cartUrl, { signal }).then((response) => response.json());
      const items = cart.items || [];

      if (items.length === 0) {
        this.hidden = true;
        return;
      }

      // Seeded from the most recently added line — the strongest signal for
      // "what goes with this".
      const seedId = items[items.length - 1].product_id;
      const cartProductIds = items.map((item) => String(item.product_id));

      let products = await this.#requestRecommendations(seedId, 'complementary', signal);
      if (products.length === 0) {
        products = await this.#requestRecommendations(seedId, 'related', signal);
      }

      const entries = this.#usable(products, cartProductIds);

      if (entries.length === 0) {
        this.hidden = true;
        return;
      }

      this.#render(entries);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[cart-cross-sell] refresh failed', error);
      this.hidden = true;
    }
  }

  async #addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;

    // Naming every cart-items-component section makes Shopify return their
    // re-rendered HTML alongside the add response, which is what the theme's
    // listeners morph into place.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    if (sectionIds) formData.set('sections', sectionIds);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const addResponse = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      }).then((response) => response.json());

      if (addResponse.status) throw new Error(addResponse.message || 'Add to cart failed');

      const cart = await fetch(this.cartUrl).then((response) => response.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-cross-sell',
          sourceId: 'cart-cross-sell',
          itemCount: 1,
          productId: button.dataset.productId,
          sections: addResponse.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[cart-cross-sell] add to cart failed', error);
      deferred.reject(error);
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}